/* eslint-disable @typescript-eslint/no-unnecessary-condition */
import { Box, Skeleton, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useTenant } from "hooks/settings";
import { useGetStrategyDashboardConsumptionProjectionQuery } from "src/store/api/generatedApi";
import { OverviewCard } from "./OverviewCard";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import { Dayjs } from "dayjs";
import { ConsumptionProjectionChart } from "components/strategyDashboard/consumption/ConsumptionProjectionChart";
import { ConsumptionDisplayModeToggle } from "components/strategyDashboard/consumption/ConsumptionDisplayModeToggle";
import { ConsumptionDisplayMode } from "components/strategyDashboard/consumption/consumptionTypes";
import { useState } from "react";

export const ConsumptionProjection = ({ date }: { date: Dayjs }) => {
  const { t } = useTenantTranslation();
  const tenant = useTenant();
  const [displayMode, setDisplayMode] =
    useState<ConsumptionDisplayMode>("percentage");

  const headerContent = (
    <Box sx={{ ml: "auto" }}>
      <ConsumptionDisplayModeToggle
        value={displayMode}
        onChange={setDisplayMode}
      />
    </Box>
  );

  return (
    <OverviewCard
      title={t("consumptionProjection")}
      icon={TrendingDownIcon}
      headerContent={headerContent}
      buttonText={t("viewConsumption")}
      buttonHref={`/${tenant}/dashboard/consumption`}
    >
      <ConsumptionProjectionContent
        date={date}
        tenant={tenant}
        displayMode={displayMode}
      />
    </OverviewCard>
  );
};

const ConsumptionProjectionContent = ({
  date,
  tenant,
  displayMode,
}: {
  date: Dayjs;
  tenant: string;
  displayMode: ConsumptionDisplayMode;
}) => {
  const { t } = useTenantTranslation();
  const { data, isFetching, isLoading } =
    useGetStrategyDashboardConsumptionProjectionQuery({
      tenantName: tenant,
      on: date.toISOString(),
    });

  if (isFetching) return <Skeleton variant="rectangular" height={250} />;
  if (!data || data.values.length === 0)
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Typography variant="body1" sx={{ color: "text.secondary" }}>
          {t("pleaseDeployAPurchasingPlan")}
        </Typography>
      </Box>
    );

  return (
    <ConsumptionProjectionChart
      data={data}
      isLoading={isLoading || isFetching}
      displayMode={displayMode}
      layout={{
        height: 300,
        margin: { t: 0, b: 60, l: 120, r: 200 },
        barmode: "group",
        showlegend: true,
        legend: {
          yanchor: "top",
          y: 1,
          xanchor: "left",
          x: 1,
        },
        yaxis: {
          autorange: "reversed",
        },
        xaxis: {
          showticklabels: false,
        },
      }}
      config={{
        responsive: true,
        displayModeBar: false,
      }}
    />
  );
};
